import { Category } from '@browsing/filters';
import { storageSaveData } from '@browsing/utils';

export function findCategoryById({
    category,
    id,
}: {
    category: Category;
    id: number;
}): Category | null {
    if (category.id === id) {
        return category;
    }

    if (!category.categories || !category.categories.length) {
        return null;
    }

    for (const nextCategory of category.categories) {
        if (category.id === id) {
            return category;
        }
        const found = findCategoryById({ category: nextCategory, id });
        if (found) return found;
    }

    return null;
}

interface DefaultFilterState {
    categories: number[] | number[];
    brands: string[];
    colors: string[];
    materials: string[];
    prices: number[];
    sizes: {};
}

const defaultFilterState: DefaultFilterState = {
    brands: [],
    categories: [],
    colors: [],
    materials: [],
    prices: [],
    sizes: {},
};

enum SessionStorageKeys {
    CrossCampaignEntry = 'cross-campaign-entry',
    CrossCampaign = 'cross-campaign',
    Filters = 'filters',
}

const storageType = 'sessionStorage';

function setCrossCampaignFilters(payload: DefaultFilterState) {
    storageSaveData({
        key: 'filters',
        subKey: SessionStorageKeys.CrossCampaign,
        payload,
        overwrite: false,
        storageType,
    });
}

function setCrossCampaignFiltersCategoryId(categoryId: number) {
    setCrossCampaignFilters({ ...defaultFilterState, categories: [categoryId] });
}

export function redirectToCrossCampaignCatalog(categoryId: number) {
    setCrossCampaignFiltersCategoryId(categoryId);
    window.location.href = `/articles/categories/${[categoryId]}`;
}

export function redirectToCrossCampaignCatalogMultiCategorySelect(categoryIds: number[]) {
    setCrossCampaignFilters({ ...defaultFilterState, categories: categoryIds });
    window.location.href = `/articles/categories/${categoryIds}`;
}

enum CrossCampaignEntryKeys {
    VisitedCatalog = 'visited-catalog',
}

function setCrossCampaignEntry(key: CrossCampaignEntryKeys, payload: boolean) {
    storageSaveData({
        key: SessionStorageKeys.CrossCampaignEntry,
        subKey: key,
        payload,
        overwrite: false,
        storageType,
    });
}

function readSessionStoragekey<T = Record<string, any>>(key: SessionStorageKeys): T | null {
    try {
        return JSON.parse(sessionStorage[key]) as T;
    } catch (e) {
        return null;
    }
}

export function setVisitedCrossCampaignCatalog(payload: boolean) {
    setCrossCampaignEntry(CrossCampaignEntryKeys.VisitedCatalog, payload);
}

export function getSavedCategoryIds(): false | number[] {
    try {
        const crossCampaignEntry = readSessionStoragekey(
            SessionStorageKeys.CrossCampaignEntry,
        ) as Record<CrossCampaignEntryKeys, boolean>;

        if (!crossCampaignEntry) {
            return false;
        }

        const visitedCatalog = crossCampaignEntry[CrossCampaignEntryKeys.VisitedCatalog];

        if (!visitedCatalog) {
            return false;
        }

        const filters = readSessionStoragekey(SessionStorageKeys.Filters);

        if (!filters) {
            return false;
        }

        return (
            (filters as Record<string, { categories?: number[] }>)['cross-campaign']?.categories ||
            false
        );
    } catch (error) {
        return false;
    } finally {
        setVisitedCrossCampaignCatalog(false);
    }
}
